/* Embedded-browser (in-app webview) detection.
 *
 * Why it matters here: Google refuses OAuth inside embedded webviews
 * (`disallowed_useragent`), so a sign-in started from one can never succeed —
 * and because the refusal renders on Google's own page, it is invisible to us
 * except as a label we attach on the way out. It is used only to TAG telemetry,
 * never to gate a sign-in, so a false positive costs a mislabelled log line. */

/** Named in-app browsers, matched as substrings of the user agent. */
const IN_APP_TOKENS = [
  "FBAN", // Facebook
  "FBAV",
  "Instagram",
  "LinkedInApp",
  "Twitter",
  "Line/",
  "MicroMessenger", // WeChat
  "Snapchat",
  "Slack",
  "WhatsApp",
];

export interface EmbeddedBrowser {
  /** True when the UA looks like an embedded webview / in-app browser. */
  embedded: boolean;
  /** What matched, for triage ("android-wv", "ios-no-safari", or the app
   *  name). Empty when not embedded. */
  kind: string;
}

/** Best-effort detection, in order of confidence:
 *    - a named in-app browser token;
 *    - Android System WebView, which stamps a literal "; wv)";
 *    - iOS: a real browser always carries a "Safari/" token, an embedded
 *      WKWebView does not.
 *
 *  Heuristic on purpose — there is no capability test for "the OAuth provider
 *  will refuse this browser", and the UA is what Google itself keys on. */
export function detectEmbeddedBrowser(
  ua: string = navigator.userAgent,
): EmbeddedBrowser {
  const app = IN_APP_TOKENS.find((token) => ua.includes(token));
  if (app) return { embedded: true, kind: app.replace("/", "") };

  if (ua.includes("; wv)")) return { embedded: true, kind: "android-wv" };

  const isIOS = /iPhone|iPad|iPod/.test(ua);
  if (isIOS && !ua.includes("Safari/")) {
    return { embedded: true, kind: "ios-no-safari" };
  }

  return { embedded: false, kind: "" };
}
